import React, { useRef, useState } from 'react';
import { Upload, Loader2 } from 'lucide-react';

// I will implement without utils first to be self-contained or create the util now. 
// Standard shadcn/ui utils pattern:
/*
import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}
*/

// I'll inline the logic for now to save a file or create the util file in the same step.
// Let's create `src/lib/utils.ts` first.

interface FileUploadProps {
  onFileSelect: (file: File) => void;
  isProcessing: boolean;
}

export const FileUpload: React.FC<FileUploadProps> = ({ onFileSelect, isProcessing }) => {
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];
      if (file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {
        onFileSelect(file);
      } else {
        alert("Please upload an Excel file (.xlsx or .xls)");
      }
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      onFileSelect(e.target.files[0]);
    }
  };

  return (
    <div 
      className={`
        w-full max-w-xl mx-auto p-12 border-2 border-dashed rounded-xl transition-all duration-300 cursor-pointer flex flex-col items-center justify-center gap-4
        ${isDragging 
          ? 'border-[#21A366] bg-[#21A366]/10 scale-[1.02]' 
          : 'border-slate-300 dark:border-slate-700 hover:border-[#21A366] hover:bg-slate-50/5 dark:hover:bg-slate-800/50'
        }
      `}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onClick={handleClick}
    >
      <input 
        type="file" 
        ref={fileInputRef} 
        onChange={handleFileInput} 
        accept=".xlsx, .xls" 
        className="hidden" 
      />
      
      <div className={`p-4 rounded-full bg-slate-100 dark:bg-slate-800 mb-2 transition-transform duration-300 ${isDragging ? 'scale-110' : ''}`}>
        {isProcessing ? (
          <Loader2 className="w-10 h-10 text-[#21A366] animate-spin" />
        ) : (
          <Upload className="w-10 h-10 text-slate-400 dark:text-slate-500" />
        )}
      </div>

      <div className="text-center space-y-2">
        <h3 className="text-xl font-semibold text-slate-700 dark:text-slate-200">
          {isProcessing ? 'Processing file...' : 'Upload Excel File'}
        </h3>
        <p className="text-sm text-slate-500 dark:text-slate-400 max-w-xs mx-auto">
          Drag and drop your spreadsheet here, or click to browse.
          <br />
          <span className="text-xs text-black mt-1 block">Supports .xlsx and .xls</span>
        </p>
      </div>
    </div>
  );
};
